import React, { useMemo, useState, useEffect } from "react";
import { observer } from "@formily/react";
import { IGridColumnProps } from "@formily/antd-v5";

// Create our own interface that doesn't extend IFormGridProps to avoid the columnGap conflict
interface IRumbleFormGridProps {
  maxColumns?: number | number[];
  breakpoints?: number[];
  columnGap?: number | number[]; // Now we can make this responsive
  rowGap?: number | number[]; // Also make rowGap responsive
  style?: React.CSSProperties;
  className?: string;
  // Add other common props you might need
  prefixCls?: string;
}

const useResponsiveColumns = (
  maxColumns: number | number[],
  breakpoints: number[] = [768],
) => {
  const [currentColumns, setCurrentColumns] = useState(() => {
    if (Array.isArray(maxColumns)) {
      return maxColumns[maxColumns.length - 1]; // Default to desktop
    }
    return maxColumns || 3;
  });

  useEffect(() => {
    if (!Array.isArray(maxColumns) || !breakpoints.length) return;

    const handleResize = () => {
      const width = window.innerWidth;
      let breakpointIndex = breakpoints.length; // Default to last (desktop)

      for (let i = 0; i < breakpoints.length; i++) {
        if (width <= breakpoints[i]) {
          breakpointIndex = i;
          break;
        }
      }

      const columns = Array.isArray(maxColumns)
        ? maxColumns[breakpointIndex] || maxColumns[maxColumns.length - 1]
        : maxColumns;

      setCurrentColumns(columns);
    };

    handleResize(); // Initial call
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [maxColumns, breakpoints]);

  return currentColumns;
};

const useResponsiveGap = (
  gap: number | number[],
  breakpoints: number[] = [768],
) => {
  const [currentGap, setCurrentGap] = useState(() => {
    if (Array.isArray(gap)) {
      return gap[0]; // Default to mobile (first value)
    }
    return gap || 16;
  });

  useEffect(() => {
    if (!Array.isArray(gap) || !breakpoints.length) return;

    const handleResize = () => {
      const width = window.innerWidth;
      let breakpointIndex = 0; // Start with mobile (first index)

      // Find the appropriate breakpoint
      for (let i = 0; i < breakpoints.length; i++) {
        if (width > breakpoints[i]) {
          breakpointIndex = i + 1;
        }
      }

      // Ensure we don't exceed array bounds
      breakpointIndex = Math.min(breakpointIndex, gap.length - 1);

      const currentGap = gap[breakpointIndex];
      setCurrentGap(currentGap);
    };

    handleResize(); // Initial call
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [gap, breakpoints]);

  return currentGap;
};

export const RumbleFormGrid: React.FC<
  React.PropsWithChildren<IRumbleFormGridProps>
> = observer(
  ({
    children,
    maxColumns = 3,
    breakpoints = [768],
    columnGap = 16,
    rowGap = 8,
    style,
    className,
    ...props
  }) => {
    const responsiveColumns = useResponsiveColumns(maxColumns, breakpoints);
    const responsiveColumnGap = useResponsiveGap(columnGap, breakpoints);
    const responsiveRowGap = useResponsiveGap(rowGap, breakpoints);

    // Create CSS Grid template based on current columns and gaps
    const gridStyle = useMemo(
      () => ({
        display: "grid",
        gridTemplateColumns: `repeat(${responsiveColumns}, 1fr)`,
        columnGap: `${responsiveColumnGap}px`,
        rowGap: `${responsiveRowGap}px`,
        ...style,
      }),
      [responsiveColumns, responsiveColumnGap, responsiveRowGap, style],
    );

    return (
      <div
        className={`rumble-form-grid ${className || ""}`.trim()}
        style={gridStyle}
        {...props}
      >
        {children}
      </div>
    );
  },
);

// GridColumn component that actually works with CSS Grid
export const RumbleGridColumn: React.FC<
  React.PropsWithChildren<
    IGridColumnProps & {
      responsiveSpan?: number | number[];
    }
  >
> = observer(
  ({ gridSpan = 1, responsiveSpan, children, style, className, ...props }) => {
    const [currentSpan, setCurrentSpan] = useState(gridSpan);

    useEffect(() => {
      if (!Array.isArray(responsiveSpan)) return;

      const handleResize = () => {
        const width = window.innerWidth;
        const breakpoints = [768]; // You can make this configurable

        let breakpointIndex = breakpoints.length;
        for (let i = 0; i < breakpoints.length; i++) {
          if (width <= breakpoints[i]) {
            breakpointIndex = i;
            break;
          }
        }

        const span = Array.isArray(responsiveSpan)
          ? responsiveSpan[breakpointIndex] ||
            responsiveSpan[responsiveSpan.length - 1]
          : responsiveSpan || gridSpan;

        setCurrentSpan(span);
      };

      handleResize();
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, [responsiveSpan, gridSpan]);

    const columnStyle = useMemo(
      () => ({
        gridColumn: `span ${currentSpan}`,
        ...style,
      }),
      [currentSpan, style],
    );

    return (
      <div
        className={`rumble-grid-column ${className || ""}`.trim()}
        style={columnStyle}
        {...props}
      >
        {children}
      </div>
    );
  },
);

// Compose the component with static methods like the original FormGrid
type ComposedRumbleFormGrid = typeof RumbleFormGrid & {
  GridColumn: typeof RumbleGridColumn;
};

(RumbleFormGrid as ComposedRumbleFormGrid).GridColumn = RumbleGridColumn;

export default RumbleFormGrid as ComposedRumbleFormGrid;
